package com.essntl.features.organization.ui.organizationdetails

import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.PaginationState
import com.essntl.core.utils.viewmodel.State
import com.essntl.core.utils.viewmodel.ViewModel
import com.essntl.core.utils.viewmodel.getData
import com.essntl.core.utils.viewmodel.getPaginationData
import com.essntl.features.contact.domain.repository.ContactRepository
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.file.ui.FileViewModel
import com.essntl.features.organization.domain.model.OrganizationModel
import com.essntl.features.organization.domain.repository.OrganizationRepository
import com.essntl.toastmanager.ToastManager
import com.mohamedrejeb.calf.core.PlatformContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class OrganizationDetailsViewModel(
    private val state: MutableStateFlow<OrganizationDetailsState>,
    private val organizationRepository: OrganizationRepository,
    private val contactRepository: ContactRepository,
    private val fileRepository: FileRepository,
    private val toastManager: ToastManager,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    private val fileViewModel = FileViewModel(
        fileRepository = fileRepository,
        scope = scope,
        loadingManager = loadingManager,
    )

    init {
        collectFileState()
    }

    private var getLogoJob: Job? = null
    private fun collectFileState() {
        // collecting logos
        getLogoJob?.cancel()
        getLogoJob = scope.launch {
            fileViewModel.state.collect { data ->
                state.update {
                    it.copy(
                        fileState = data,
                    )
                }
            }
        }
    }

    fun onEvent(event: OrganizationDetailsEvent) {
        when (event) {
            is OrganizationDetailsEvent.GetOrganization -> getOrganization(event)
            is OrganizationDetailsEvent.UpdateContact -> updateContact(event)
            is OrganizationDetailsEvent.DeleteContact -> deleteContact(event)
            is OrganizationDetailsEvent.LoadParentOrganizations -> getNextOrganizationPage()
            is OrganizationDetailsEvent.SearchParentOrganization -> {
                state.value = state.value.copy(getAllOrganizations = PaginationState(query = event.query))
                getNextOrganizationPage(type = event.type)
            }

            is OrganizationDetailsEvent.AddLogo -> addLogo(event)
            is OrganizationDetailsEvent.UpdateOrganizationFields -> updateOrganizationFields(event)
            is OrganizationDetailsEvent.EditOrganization -> editOrganization(state.value.organization.value)
            is OrganizationDetailsEvent.UpdateParentOrganization -> updateParentOrganization(event)
            is OrganizationDetailsEvent.InsertLogo -> insertFiles(event.context)
            is OrganizationDetailsEvent.DeleteLogo -> deleteLogo()
            is OrganizationDetailsEvent.SelectContact -> selectContact(event)
            is OrganizationDetailsEvent.ClearSuccessState -> clearSuccessState()
            is OrganizationDetailsEvent.AddRemoveAddress -> addRemoveAddress(event)
        }
    }

    private fun updateOrganizationFields(event: OrganizationDetailsEvent.UpdateOrganizationFields) {
        state.value.organization.value = event.organization
    }

    private fun updateParentOrganization(event: OrganizationDetailsEvent.UpdateParentOrganization) =
        state.update {
            it.copy(
                selectedParentOrganization = event.organization,
            )
        }

    private fun getNextOrganizationPage(
        query: String = state.value.getAllOrganizations.query,
        type: OrganizationModel.Type? = state.value.organization.value.type,
    ) = getPaginationData(
        getData = {
            val nextPage = state.value.getAllOrganizations.loadedPages + 1L

            organizationRepository.getAll(
                page = nextPage,
                query = query,
                type = type,
            )
        },
        getState = { state.value.getAllOrganizations },
        setState = { state.value = state.value.copy(getAllOrganizations = it) },
    )

    private fun getOrganization(event: OrganizationDetailsEvent.GetOrganization) =
        getData(
            getData = { organizationRepository.get(id = event.id) },
            getState = { state.value.getOrganization },
            setState = { newState -> state.update { it.copy(getOrganization = newState) } },
            onSuccess = {
                state.value.organization.value = it

                val logo = it.logoFileId

                if (!logo.isNullOrEmpty())
                    fileViewModel.getFiles(listOf(logo)) else
                    fileViewModel.setState(State.success(emptyList()))

                getContacts(organizationId = it.id)

                it.parentId?.let { parentId ->
                    organizationRepository.get(parentId)
                        .onSuccess { o ->
                            state.update {
                                it.copy(
                                    selectedParentOrganization = o,
                                )
                            }
                        }
                }
            },
        )

    private fun editOrganization(
        organization: OrganizationModel,
        onSuccess: () -> Unit = {},
    ) =
        getData(
            getData = { organizationRepository.update(organization) },
            getState = { state.value.editOrganization },
            setState = { newState -> state.update { it.copy(editOrganization = newState) } },
            onSuccess = { o ->
                state.update {
                    it.copy(
                        getOrganization = State.success(o),
                    )
                }

                state.value.organization.value = o

                onSuccess()

                toastManager.success(description = "Organization updated successfully")
            },
        )

    private fun getContacts(organizationId: String) =
        getData(
            getData = { contactRepository.getByOrganizationId(id = organizationId) },
            getState = { state.value.getAllContacts },
            setState = { newState -> state.update { it.copy(getAllContacts = newState) } },
        )

    private fun updateContact(event: OrganizationDetailsEvent.UpdateContact) =
        state.update {
            it.copy(
                getAllContacts = state.value.getAllContacts.copy(
                    data = state.value.getAllContacts.data?.map {
                        if (it.id == event.contact.id)
                            event.contact else
                            it
                    },
                ),
            )
        }

    private fun deleteContact(event: OrganizationDetailsEvent.DeleteContact) =
        state.update {
            it.copy(
                getAllContacts = state.value.getAllContacts.copy(
                    data = state.value.getAllContacts.data?.filter {
                        it.id != event.contact.id
                    },
                ),
            )
        }

    private fun addLogo(event: OrganizationDetailsEvent.AddLogo) {
        if (event.file.id.isNotEmpty()) {
            val organization = state.value.organization.value.copy(
                logoFileId = event.file.id,
            )

            editOrganization(
                organization = organization,
            )
        }

        fileViewModel.addFiles(listOf(event.file))
        insertFiles(event.context)
    }

    private fun deleteLogo() {
        scope.launch {
            val organization = state.value.organization.value.copy(
                logoFileId = "",
            )

            editOrganization(
                organization = organization,
                onSuccess = {
                    fileViewModel.deleteFile(0)
                },
            )
        }
    }

    fun insertFiles(context: PlatformContext) =
        fileViewModel.insertOrganizationLogos(
            setState = { newState ->
                state.update {
                    it.copy(
                        uploadLogo = newState,
                    )
                }
            },
            onSuccess = { files ->
                val organization = state.value.organization.value.copy(
                    logoFileId = files.first().id,
                )

                editOrganization(
                    organization = organization,
                )
            },
            folderName =
                if (state.value.organization.value.type == OrganizationModel.Type.TravelAgency)
                    "ta_logos" else
                    "supplier_logos",
            entityType = if (state.value.organization.value.type == OrganizationModel.Type.TravelAgency)
                "travel_agent" else
                "supplier",
            organizationName = state.value.organization.value.name,
            context = context,
        )

    fun clearSuccessState() =
        state.update {
            it.copy(
                uploadLogo = State(),
                editOrganization = State(),
            )
        }

    private fun selectContact(event: OrganizationDetailsEvent.SelectContact) =
        state.update {
            it.copy(
                selectedContactId = event.id,
            )
        }

    private fun addRemoveAddress(event: OrganizationDetailsEvent.AddRemoveAddress) =
        state.update {
            it.copy(
                addAddress = event.addAddress ?: !it.addAddress,
            )
        }
}